import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Meteor } from 'meteor/meteor';
import { strings } from '/imports/strings';
import { mapById } from '/imports/utils';
import { dateTimeFormatOptions, languageOptions, themeOptions, timezoneOptions } from '/imports/options';
import { Accounts } from '/imports/api/accounts';
import { Orgs } from '/imports/api/orgs';
import { Publications } from '/imports/publications';
import { Button } from '/imports/ui/Button';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { ImageInput } from '/imports/ui/ImageInput';
import { deleteMyUser, setSetting, updateUserProfile } from '/imports/api/users';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useFind, useFindOne, useSubscribed, useUser } from '/imports/hooks';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { autoForm, formConfig } from '/imports/ui/AutoForm';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { toastOnError } from '/imports/ui/utils';

// Tailwind shell replacing the old styled(Page) wrapper. Descendant rules reach
// the .avatar / .theme / .file-widget-thumb elements rendered by AutoForm and
// ImageInput; .account-links / .org-settings are in-file (classed directly).
const ProfilePageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[
      props.className ?? '',
      '[&_.avatar]:flex-[2.5]',
      '[&_.theme]:hidden!',
      '[&_.field-group_.wrapper_.file-widget-thumb]:ml-0',
    ].join(' ')}
  />
);

export const ProfilePage = () => {
  const user = useUser();
  const userId = user?._id;
  const orgsLoading = useSubscribed(Publications.listMyOrgs);
  const accounts = useFind(() => Accounts.find({ userId }), [userId, orgsLoading]);
  const orgs = useFind(() => Orgs.find({ _id: { $in: accounts.map(a => a.orgId) } }), [accounts]);
  const orgMap = useMemo(() => mapById(orgs), [orgs]);
  const org = useFindOne(() => Orgs.find(user?.orgId), [user?.orgId]);
  const [theme, setTheme] = useState(user?.settings?.theme || 'light');
  const [deletingUser, setDeletingUser] = useState(false);
  const form = formConfig<Meteor.User>([
    [
      { field: 'avatar', input: { type: ImageInput, props: { aspect: 1 } }, label: strings.ProfilePicture() },
      <FormField key="theme" className="theme" field="theme">
        <label>{strings.Theme()}</label>
        <Select id="profile-theme" value={theme || user?.settings?.theme || 'light'}
          options={themeOptions()}
          onChange={(theme) => setTheme(theme)}
        />
      </FormField>
    ],
    [
      { field: 'username' },
      { field: 'password', input: { type: Input, props: { type: 'password' } } }
    ],
    [
      { field: 'email' },
      { field: 'phone', input: { type: PhoneInput } },
      { field: 'firstName' },
      { field: 'lastName' }
    ],
    [
      { field: 'language', input: { type: Select, props: { options: languageOptions() } } },
      { field: 'timezone', input: { type: Select, props: { options: timezoneOptions() } } },
      { field: 'dateTimeFormat', input: { type: Select, props: { options: dateTimeFormatOptions() } } },
    ]
  ]);
  const [ProfileForm] = autoForm({
    document: user,
    createMethod: updateUserProfile as any,
    updateMethod: updateUserProfile as any,
    form,
    extraButtons: (
      <>
        <Button onClick={() => setDeletingUser(true)} className="btn delete">{strings.DeleteMyAccount()}</Button>
      </>
    )
  });
  useEffect(() => {
    setSetting.call({ key: 'theme', value: theme });
  }, [theme]);
  const deleteMe = toastOnError(async () => {
    await deleteMyUser.call();
    toast.success(strings.Done());
    Meteor.logout();
  });
  const initialized = user && !orgsLoading;
  if (!initialized) return <LoadingFlex />;
  return (
    <ProfilePageStyled className={`page${!initialized ? ' initializing' : ''}`}
      breadcrumbs={[{ path: '/profile', label: strings.Profile() }]}
    >
      <PageHeading
        left={<h1>{strings.Profile()}</h1>}
      />
      <PageBody>
        {ProfileForm}
        <div className="account-links relative hidden pb-12 [&_.btn]:mr-4">
          {
            accounts.map((account) => {
              const org = orgMap[account.orgId];
              return (
                <Link key={account._id} to={`/profile/${account._id}`}>
                  <Button>{org?.name} {strings.Settings()}</Button>
                </Link>
              );
            })
          }
        </div>
        {
          org ?
            <Link className="org-settings mt-4" to={'/my-org'}>
              <Button>{org?.name} {strings.Settings()}</Button>
            </Link>
            :
            null
        }
      </PageBody>
      {
        deletingUser ?
          <ConfirmModal
            title={strings.DeleteMyAccount()}
            body={<p>{strings.AreYouSureYouWantToDeleteYourAccount()}</p>}
            onConfirm={() => deleteMe()}
            onClose={() => setDeletingUser(false)}
          />
          :
          null
      }
    </ProfilePageStyled>
  );
};